import { DatePipe } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { PageEvent } from '@angular/material/paginator';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { State, Store } from '@ngrx/store';
import { ReservationStatus, TransitProgram, Trip, VehicleType } from '@uzurv/clara';
import { isNullOrUndefined, ReservationStatusNameConstants, ServiceLevelConstants, VehicleTypeConstants } from '@uzurv/core';
import { UzurvTableColumnDefinition } from '@uzurv/http';
import { timer } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';
import { APP_CONFIGURATION, AppConfiguration } from '../../app/app-configuration';
import { GenericListIconDefinition } from '../../components/generic-list/generic-list-icon';
import {
  ChangeTab,
  GetRides,
  PageRides,
  SelectReservationStatuses,
  SelectTransitPrograms,
  SelectVehicleTypes,
  Start
} from '../../store/ride-tracking/ride-tracking.actions';
import { RideTrackingState } from '../../store/ride-tracking/ride-tracking.reducer';
import {
  getCurrentTab,
  getLoading,
  getPageNumber,
  getPageSize,
  getReservationStatuses,
  getRides,
  getSelectedReservationStatuses,
  getSelectedTransitPrograms,
  getSelectedVehicleTypes
} from '../../store/ride-tracking/ride-tracking.selectors';
import { OpenTripDetailTab } from '../../store/trip-detail/trip-detail.actions';
import { BasePage } from '../base/base.page';

export enum Tabs {
  PENDING = 'Pending Ride Requests',
  ACTIVE = 'Active Reservations',
  CANCELLED = 'Cancelled Reservations'
}

@Component({
  selector: 'app-ride-tracking',
  templateUrl: './ride-tracking.page.html',
  styleUrls: ['./ride-tracking.page.scss'],
  providers: [DatePipe]
})
export class RideTrackingPageComponent extends BasePage implements OnInit {
  loading$ = this.store$.select(getLoading);
  currentTab$ = this.store$.select(getCurrentTab);
  pageNumber$ = this.store$.select(getPageNumber);
  pageSize$ = this.store$.select(getPageSize);
  reservationStatuses$ = this.store$.select(getReservationStatuses);
  selectedTransitProgramIds$ = this.store$.select(getSelectedTransitPrograms);
  selectedVehicleTypeIds$ = this.store$.select(getSelectedVehicleTypes);
  selectedReservationStatusIds$ = this.store$.select(getSelectedReservationStatuses);
  rides$ = this.store$.select(getRides);

  tabs = Tabs;
  statusSelector = new UntypedFormControl();
  columns: Array<UzurvTableColumnDefinition> = [];

  constructor(private store$: Store<State<RideTrackingState>>, @Inject(APP_CONFIGURATION) private appConfiguration: AppConfiguration) {
    super();
  }

  ngOnInit() {
    this.store$.dispatch(Start());
    this.selectedReservationStatusIds$
      .pipe(
        takeUntil(this.destroyed$),
        map(selectedReservationStatuses => this.statusSelector.setValue(selectedReservationStatuses, { onlySelf: true, emitEvent: false }))
      )
      .subscribe();

    this.statusSelector.valueChanges
      .pipe(
        takeUntil(this.destroyed$),
        map(statuses => {
          this.store$.dispatch(SelectReservationStatuses({ reservationStatuses: statuses }));
        })
      )
      .subscribe();

    timer(0, this.appConfiguration.listPollingSeconds * 1000)
      .pipe(
        takeUntil(this.destroyed$),
        map(() => this.store$.dispatch(GetRides()))
      )
      .subscribe();

    this.currentTab$
      .pipe(
        takeUntil(this.destroyed$),
        filter(tab => !isNullOrUndefined(tab)),
        map(tab => {
          this.columns = [
            {
              columnDef: 'transitProgramCode',
              title: 'Transit Program',
              cell: (ride: Trip) => ride.transitProgramCode
            },
            { columnDef: 'rider', title: 'Rider', cell: (ride: Trip) => ride.riderName },
            ...this.shouldShowDriverColumn(tab),
            {
              columnDef: 'pickupDate',
              title: 'Pick-up Date',
              cell: (ride: Trip) => ride.formattedDisplayDate
            },
            { columnDef: 'status', title: 'Status', cell: (ride: Trip) => ride.status },
            { columnDef: 'rideBackup', title: 'Ride Backup', cell: (ride: Trip) => this.getRideBackupStatus(ride) },
            { columnDef: 'badges', title: '', cell: (ride: Trip) => this.getBadgeHtml(ride) }
          ];
        })
      )
      .subscribe();
  }

  // View
  handleRefreshRides(): void {
    this.store$.dispatch(GetRides());
  }

  handleTransitProgramSelection(event: Array<TransitProgram>): void {
    this.store$.dispatch(SelectTransitPrograms({ transitPrograms: event }));
  }

  handleVehicleTypeSelection(event: Array<VehicleType>): void {
    this.store$.dispatch(SelectVehicleTypes({ vehicleTypes: event }));
  }

  handleChangeTab(event: MatTabChangeEvent): void {
    this.store$.dispatch(ChangeTab({ tabs: event.tab.textLabel as Tabs }));
  }

  handlePageRides(pageEvent: PageEvent): void {
    this.store$.dispatch(PageRides({ pageEvent }));
  }

  handleShowDetails(row: Trip): void {
    this.store$.dispatch(OpenTripDetailTab({ payload: { trip: row } }));
  }

  getReservationStatusCssName(status: ReservationStatus): string {
    return status.name.split(' ').join('');
  }

  getTabOrdinalFromName(tab: Tabs): number {
    return Object.values(Tabs).indexOf(tab);
  }

  /**
   * TODO: remove when trip has a reference to chat messages
   */
  getBadgeHtml(trip: Trip): GenericListIconDefinition {
    if (trip.hasNewChatMessages) {
      return new GenericListIconDefinition('chat_bubble');
    }

    return null;
  }

  /**
   * Determine ride backup status for a given ride.
   */
  getRideBackupStatus(trip: Trip): string {
    if (!trip.rideBackupEligible) {
      return 'N/A';
    }

    const isRideBackupAccepted =
      trip.minimumServiceLevel.code === ServiceLevelConstants.AMBULATORY_CURB_TO_CURB_CODE ||
      trip.minimumServiceLevel.code === ServiceLevelConstants.NON_AMBULATORY_CURB_TO_CURB_CODE;

    if (isRideBackupAccepted && trip.status === ReservationStatusNameConstants.PENDING) {
      if (!trip.lyftAutoDispatchEnabled) {
        return 'Needs Manual Dispatch';
      } else {
        return 'Yes';
      }
    }

    return 'No';
  }

  compareIds(o1: any, o2: any): boolean {
    if (isNullOrUndefined(o1) || isNullOrUndefined(o2)) {
      return false;
    }
    return o1.id === o2.id;
  }

  private shouldShowDriverColumn(tab: Tabs) {
    return tab !== Tabs.PENDING
      ? [
          {
            columnDef: 'driver',
            title: 'Driver',
            cell: (ride: Trip) =>
              ride.vehicleTypeCode === VehicleTypeConstants.VEHICLE_TYPE_CODE_WAV && !isNullOrUndefined(ride.providerName)
                ? ride.providerName
                : ride.driverName
          }
        ]
      : [];
  }
}
